/**
 * D6 — Hub roteado do turno (spec-010-supervisor-e-agentes-de-dominio, [T025]).
 *
 * Espelho FRONT do enum `Hub` do backend
 * (`aawz_intelligence_v2/contratos/modelos/contexto.py`) e do catálogo de
 * agentes (`aawz_intelligence_v2/agentes/catalogo/*.yaml`). Convive com
 * `constants/Personas.js` (o valor gravado em `context`) e `constants/Agentes.js`
 * (o catálogo do seletor): hub é o eixo pelo qual a UI EXIBE quem respondeu
 * quando o Supervisor roteia. Contrato: `contracts/ui-components.md` §4.
 *
 * `Hubs.unit.js` é o drift-guard: deriva os 3 ids de hub de
 * `constants/hubs.py` (nunca escreve a lista ao lado) e o mapa
 * `rotuloDoAgente` do diretório `agentes/catalogo/` — mesmo mecanismo de
 * `Personas.unit.js`, e a razão pela qual o Especialista Fee-Based ficou de
 * fora do espelho uma vez (M-1 da review de arquitetura).
 */

export const HUB_PROFISSIONAL = 'profissional'
export const HUB_GESTAO = 'gestao'
export const HUB_SERVICOS = 'servicos'
// AI_FRAMEWORK permanece como valor legado de `context` até a onda B fechar
// ([T031] apaga `constants/personas.py`) — este módulo não referencia esse
// valor de propósito.

/**
 * Registro por hub. `permiteHtml`/`mostraFontes` só `true` no Hub do
 * Profissional — decisão de segurança HERDADA das personas (`permiteHtml` vira
 * `allow-html` no renderizador, que só então roda o DOMPurify): falha FECHADA
 * nos outros dois hubs, mesmo que um agente novo passe a rotear para lá.
 */
export const HUBS = {
  [HUB_PROFISSIONAL]: {
    id: HUB_PROFISSIONAL,
    label: 'Hub do Profissional',
    iconName: 'ia-w-mark',
    permiteHtml: true,
    mostraFontes: true,
  },
  [HUB_GESTAO]: {
    id: HUB_GESTAO,
    label: 'Hub de Gestão',
    iconName: 'ia-w-mark',
    permiteHtml: false,
    mostraFontes: false,
  },
  [HUB_SERVICOS]: {
    id: HUB_SERVICOS,
    label: 'Hub de Serviços',
    iconName: 'ia-w-mark',
    permiteHtml: false,
    mostraFontes: false,
  },
}

/**
 * Resolve o hub pelo id, caindo no default (Hub do Profissional — mesmo
 * default de `hub_da_conversa`/`hub_default_do_agente` em
 * `constants/hubs.py::HUB_DEFAULT = Hub.PROFISSIONAL`) para id ausente ou
 * desconhecido. NUNCA lança.
 *
 * @param {String} id Id do hub.
 * @returns {Object} Registro do hub.
 */
export function getHub(id) {
  if (id && Object.hasOwn(HUBS, id)) return HUBS[id]
  return HUBS[HUB_PROFISSIONAL]
}

/**
 * slug do agente (`agentes/catalogo/*.yaml` → campo `slug`) -> `nome` do
 * catálogo. Espelho estático travado pelo drift-guard de `Hubs.unit.js` —
 * agente novo no catálogo do backend sem entrada aqui acende o teste.
 *
 * FONTE DA VERDADE: campo `nome:` de cada
 * `aawz_intelligence_v2/agentes/catalogo/<slug>.yaml` no backend. Para
 * regenerar após o catálogo mudar, rode no checkout do backend:
 * `grep -H '^nome:' aawz_intelligence_v2/agentes/catalogo/*.yaml` e copie o
 * valor exato (com acentos) para a entrada correspondente aqui — o
 * drift-guard falha byte a byte se divergir.
 */
const ROTULO_POR_SLUG = {
  'accounting-advisor': 'Contabilidade e Tributário',
  'analista-reuniao': 'Analista de Reunião AAWZ',
  // `billing` deixou de ser "Especialista Fee-Based" na spec-016
  // (agente-especialista-fee-fixo): a faceta de fee fixo virou o agente
  // `fee-fixo` abaixo, e `billing` ficou só com a cobrança do consolidador.
  billing: 'Cobrança do Consolidador',
  'client-relationship': 'IA Operacional',
  'commercial-advisor': 'Comercial e Captação',
  compliance: 'Compliance',
  controller: 'Controller',
  'extrator-documentos': 'Extrator de Documentos AAWZ',
  'fee-fixo': 'Especialista em Fee Fixo',
  'finance-advisor': 'Financeiro',
  'financial-planning': 'Financial Planning',
  knowledge: 'Conhecimento Oficial do Escritório',
  'legal-advisor': 'Jurídico e Regulatório',
  'ma-advisor': 'Fusões e Aquisições',
  'management-intelligence': 'Management Intelligence',
  'marketing-advisor': 'Marca e Aquisição',
  'partnership-compensation': 'Partnership & Compensation',
  'people-advisor': 'Pessoas, Carreira e Partnership',
  'strategic-advisor': 'Consultor Estratégico',
  'strategy-advisor': 'Estratégia',
  supervisor: 'Supervisor AAWZ',
  'technology-advisor': 'Tecnologia e Operação',
  'wealth-intelligence': 'Wealth Intelligence',
}

/**
 * Rótulo humano do agente que respondeu o turno, a partir do `agent_slug` do
 * SSE/persistido (`ui-components.md` §1.1: "`agentLabel` ausente ⇒ derivado
 * do slug por `Hubs.js`"). Slug ausente/desconhecido devolve string vazia —
 * NUNCA lança — e o `agent-badge` cai no estado só-hub (mesmo comportamento
 * de turno antigo sem `agent_slug`).
 *
 * @param {String} agentSlug Slug do agente (`_agentes.slug`).
 * @returns {String} Rótulo humano, ou `''` se desconhecido.
 */
export function rotuloDoAgente(agentSlug) {
  if (agentSlug && Object.hasOwn(ROTULO_POR_SLUG, agentSlug)) {
    return ROTULO_POR_SLUG[agentSlug]
  }
  return ''
}
